import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
} from '@mui/material';

const theme = createTheme({
  typography: {
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
});

function WorkfsdWebsite() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  return (
    <ThemeProvider theme={theme}>
      <Box>


      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 4, md: 6 },
          pb: 3,
          textAlign: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
              backgroundColor: 'transparent'
            }}
          >
            <img 
              src="/surfboard.svg" 
              alt="Surfboard"
              style={{
                height: '120px',
                width: 'auto',
                backgroundColor: 'transparent'
              }}
            />
          </Box>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              mb: 3,
              color: 'primary.main',
              fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}
          >
           Work on your tabs like surfing
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              mb: 3,
              maxWidth: '800px',
              mx: 'auto',
              color: 'text.secondary',
              lineHeight: 1.6,
              fontSize: { xs: '1.1rem', md: '1.25rem' }
            }}
          >
            Too many tabs open? Can't find what matters? Surfboard organizes your browser tabs and keeps you focused on your most important work.
          </Typography>
        </Container>
      </Box>

      {/* Demo Section */}
      <Box
        id="demo"
        sx={{
          pb: 6,
          backgroundColor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
              borderRadius: 4,
              boxShadow: 6,
              maxWidth: '900px',
              mx: 'auto'
            }}
          >
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/WJoZK9sMwvw?rel=0"
              title="Surfboard Demo"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 4
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </Box>
        </Container>
      </Box>

      {/* Vision Section */}
      <Box
        id="vision"
        sx={{
          py: 6,
          backgroundColor: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, color: 'text.primary' }}
          >
            Our Vision
          </Typography>
          <Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.7,
                fontSize: '1.2rem'
              }}
            >
              AGI will be built from—and for—everyone’s small, everyday tasks. Starting with tab management, we're automating every corner of the long tail. Over time, this evolves into an AGI capable of navigating any workflow, just like Level 5 self-driving navigates the roads.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Team Section */}
      <Box
        id="team"
        sx={{
          py: 10,
          backgroundColor: 'background.default',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 8, color: 'text.primary' }}
          >
            Our Team
          </Typography>
          <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
            <Card 
              elevation={2}
              sx={{ 
                p: 4,
                textAlign: 'center',
                backgroundColor: 'white'
              }}
            >
              <CardContent>
                <Typography variant="h4" gutterBottom sx={{ color: 'text.primary' }}>
                  Wei Duan
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{
                    mb: 3,
                    fontWeight: 600
                  }}
                >
                  CEO & Founder
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.6
                  }}
                >
                  Serial entrepreneur with AI experience at Google and Snapchat. Building tools that make work easier and more productive.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        id="contact"
        sx={{
          backgroundColor: 'grey.900',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 6,
              mb: 6
            }}
          >
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Workfsd
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  opacity: 0.8,
                  lineHeight: 1.6
                }}
              >
                Building AI for everyday work problems
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>
                Get in Touch
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  opacity: 0.8,
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                Email: weiduan1025@gmail.com
              </Typography>
            </Box>
          </Box>
          <Box 
            sx={{ 
              textAlign: 'center', 
              pt: 4, 
              borderTop: '1px solid rgba(255,255,255,0.1)' 
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © 2025 Workfsd. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
      </Box>
    </ThemeProvider>
  );
}

export default WorkfsdWebsite;